import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, Loader2, Eye, EyeOff, Lock, Mail, Trophy } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const AcademyCreateAccount = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setError('No session ID provided. Please complete payment first.');
      setLoading(false);
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const response = await api.get(`/academy/enrollment/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentVerified(true);
        setPaymentInfo(response.data);
      } else {
        setError('Payment not completed. Please complete your payment first.');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setError('Could not verify payment. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/academy/create-account', {
        session_id: sessionId,
        password: password
      });

      if (response.data.success) {
        // Store auth data
        localStorage.setItem('academy_token', response.data.token);
        localStorage.setItem('academy_user', JSON.stringify(response.data.user));
        
        toast.success('Account created! Check your email for your Letter of Intent.');
        
        // Navigate to dashboard
        navigate('/academy/high-performance');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create account';
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
          <p className="text-gray-400">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      <div className="container mx-auto px-6 py-16 max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="/images/scholar-athlete-logo.png" 
            alt="Scholar Athlete Academy" 
            className="h-32 w-auto"
            style={{ filter: 'invert(37%) sepia(100%) saturate(2500%) hue-rotate(2deg) brightness(105%) contrast(105%)' }}
          />
        </div>

        {paymentVerified ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <CardTitle className="text-white text-2xl">Payment Successful!</CardTitle>
              <p className="text-gray-400 mt-2">
                Create your account to access the {paymentInfo?.tier_name} program.
              </p>
            </CardHeader>
            <CardContent>
              {/* Payment Summary */}
              <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-green-500 font-bold">{paymentInfo?.tier_name}</span>
                    <p className="text-gray-500 text-sm">Scholar Athlete Academy</p>
                  </div>
                  <span className="text-white font-bold text-xl">
                    ${paymentInfo?.amount}
                  </span>
                </div>
              </div>

              {/* Account Creation Form */}
              <form onSubmit={handleCreateAccount} className="space-y-6">
                <div>
                  <Label className="text-white mb-2 block">Create Your Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input 
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password (min 6 characters)"
                      className="bg-white/10 border-gray-700 text-white pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-white mb-2 block">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input 
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      className="bg-white/10 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={creating}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-6"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <Trophy className="w-4 h-4 mr-2" />
                      Create Account & Enter Academy
                    </>
                  )}
                </Button>
              </form>

              <p className="text-gray-500 text-xs text-center mt-6">
                <Mail className="w-3 h-3 inline mr-1" />
                Your Letter of Intent will be sent to your email after account creation.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-gray-900 border-red-600/50">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-white text-xl font-bold mb-2">Payment Required</h2>
              <p className="text-gray-400 mb-6">{error}</p>
              <Button
                onClick={() => navigate('/academy/enroll')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Go to Enrollment
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AcademyCreateAccount;
